import { useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useSelector } from "react-redux";

import { useThunkDispatch } from "@/redux/dispatcher";
import * as CustomerModule from "@/redux/modules/api/data/customer/customer.module";
import { getCustomer } from "@/redux/modules/api/data/customer/customer.selectors";
import { EcornerService } from "@/services/eCorner.service";
import { Config } from "@/utils/config.utils";

import InactivityMonitor from "./inactivityMonitor.component";

const inactivityCountdown = 1000 * 60 * 6;
const modalLogoutCountdown = 60;

const InactivityMonitorContainer = () => {
    const dispatch = useThunkDispatch();
    const [isModalOpen, setModalOpen] = useState<boolean>(false);
    const [timeLeft, setTimeLeft] = useState<number>(0);
    const customer = useSelector(getCustomer);

    const handleOnIdle = () => {
        setModalOpen(true);
    };

    const { reset, start } = useIdleTimer({
        timeout: inactivityCountdown,
        onIdle: handleOnIdle,
        startOnMount: false,
        startManually: true,
        stopOnIdle: true
    });

    const toggleModal = (state: boolean) => {
        setModalOpen(state);
        if (!state) {
            reset();
            // dependency doesn't seem to reset properly when only using the reset method
            start();
        }
    };

    const endUserSession = async () => {
        await dispatch(CustomerModule.logout());
        setModalOpen(false);
        location.assign(Config.pages.home);
    };

    const calculateTimeLeft = () => {
        const tempTime = timeLeft - 1;
        if (tempTime <= 0) {
            endUserSession();
        } else {
            setTimeLeft(tempTime);
        }
    };

    useEffect(() => {
        EcornerService.isEcornerEnvironment().then((ecorner) => {
            if (!ecorner) return;

            if (!customer) {
                dispatch(CustomerModule.load());
            } else {
                start();
            }
        });
    }, [customer, dispatch, start]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (timeLeft > 0 && isModalOpen) {
            const timer = setTimeout(() => {
                calculateTimeLeft();
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [timeLeft]);

    useEffect(() => {
        if (isModalOpen) {
            setTimeLeft(modalLogoutCountdown);
        }
    }, [isModalOpen]);

    return (
        <InactivityMonitor
            inactivityCountdown={timeLeft}
            modalState={isModalOpen}
            toggleHandler={toggleModal}
            logoutUser={endUserSession}
        />
    );
};

export { InactivityMonitorContainer };
